/**
 * REGISTER / EDIT PAYMENT DEVICE — the compact SUNMI modal
 * (Terminal (EMV/SoftPOS) tab, sunmi-card-setup.md). House modal: black
 * header, ruby underline, ruby icon tile, white body, black primary button.
 * Fits 1366×768 without scrolling.
 *
 *   create: choose → Dejavoo P Series | Android Tap to Pay (→ pair code) | MagTek devices
 *   edit:   the same form pre-filled; secrets show masked with "Replace"
 *
 * Field rules come from the existing registration panel (deviceForm.ts →
 * CLASS_FORM). Writes use the existing routes only: POST …/emv-devices,
 * PATCH …/emv-devices/{id}, PUT …/emv-devices/{id}/rtpay, POST …/rtpay/pairing-code.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, CreditCard, Nfc, Info, MapPin, ChevronDown, ChevronRight, Usb } from 'lucide-react';
import { resolvePaymentConfig, paymentConfigOptionsFor } from '@platform/shared-types';
import { api } from '../../../services/apiService';
import { hardwareApi } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';
import { RubyGemMark } from '../../RubyGemMark';
import { ReaderPicker, Segmented } from './ReaderPicker';
import { tapToPayRegisterBody } from '../AndroidTapToPayCreate';
import { ANDROID_TAP_TO_PAY_MODEL, isValidMerchantCode, isValidTpn, type RtpayRegisterMode } from '../RubyTillPayRegisterFields';
import { explainPaymentError } from '../payment-types/errors';
import type { RegistrationChoice, RegistrationFlowId } from './registrationFlows';
import {
  type PForm, type SpinKey, spec, pProblems, pSeriesRegisterBody, formFromDevice, buildEditPatches, deviceTypeLabel,
} from './deviceForm';
import {
  type MForm, MAGTEK_MODELS, isMagtekDevice, magtekFormFromDevice, magtekProblems, magtekRegisterBody, magtekEditPatches,
} from './magtekForm';
import { UnigateLine, useLocationUnigate } from '../unigate/MagtekLocationCard';
import { merchantProblems } from '../sunbay/sunbayCreds';
import {
  type SunbayDeviceForm, SUNBAY_CONNECTIONS, emptySunbayForm, isSunbayDevice, sunbayFormFromDevice,
  validateSunbayForm, buildSunbayDeviceBody, sunbayEditPatches, type SunbayConnection,
  isCpadRtpayConnection, buildCpadRtpayBody,
} from './sunbayForm';

const RUBY_UNDERLINE = { background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' };
const INPUT = 'min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-900 outline-none focus-visible:outline-none placeholder:text-stone-400 focus:border-ruby focus:ring-2 focus:ring-rose-200';
const ICON: Record<RegistrationFlowId, React.ReactNode> = {
  tap_to_pay: <Nfc size={18} />, p_series: <CreditCard size={18} />, magtek: <Usb size={18} />, sunbay: <CreditCard size={18} />,
};
const SHORT: Record<RegistrationFlowId, string> = {
  tap_to_pay: 'SoftPOS', p_series: 'USB or SPIn Cloud', magtek: 'DynaFlex II PED or Go', sunbay: 'CPad Pay',
};
const P_ROUTES = [
  { id: 'dejavoo_spin_usb', label: 'USB' },
  { id: 'dejavoo_spin_cloud', label: 'SPIn Cloud' },
];

/** Every config a catalog model may register as (guarded: a stale shared-types pre-bundle lacks the helpers). */
export function configIdsOf(c: any): string[] {
  const opts = typeof paymentConfigOptionsFor === 'function' ? paymentConfigOptionsFor(c) : [];
  const ids = (opts || []).map((o: any) => o?.id).filter(Boolean);
  const d = typeof resolvePaymentConfig === 'function' ? resolvePaymentConfig(c)?.id : c?.paymentConfigId;
  return d ? [...new Set([d, ...ids])] : ids;
}
export { pSeriesRegisterBody };

/** A labelled field. A `<div>`, not a `<label>`: a wrapping label would rename the
 *  custom controls inside it; plain inputs get the label as their aria-label. */
const Field: React.FC<{ label: string; hint?: React.ReactNode; tip?: string; error?: string | null; children: React.ReactElement }> = ({ label, hint, tip, error, children }) => (
  <div className="block">
    <span className="mb-1 flex items-center gap-1 text-xs font-medium text-stone-700">
      {label}
      {tip && <span title={tip} aria-label={tip} className="inline-grid h-4 w-4 place-items-center text-stone-400"><Info size={12} /></span>}
    </span>
    {children.type === 'input' ? React.cloneElement(children as React.ReactElement<any>, { 'aria-label': label }) : children}
    {error ? <span className="mt-0.5 block text-[11px] text-ruby-deep">{error}</span>
      : hint ? <span className="mt-0.5 block text-[11px] text-stone-500">{hint}</span> : null}
  </div>
);

/** A stored secret: masked + "Replace" (reveals an empty input). Untouched = never sent. */
const Secret: React.FC<{
  label: string; masked: string | null; touched: boolean; onTouch: () => void; value: string; onChange: (v: string) => void;
  placeholder: string; error?: string | null; hint?: string; tip?: string;
}> = ({ label, masked, touched, onTouch, value, onChange, placeholder, error, hint, tip }) => (
  <Field label={label} error={error} hint={hint} tip={tip}>
    {touched || !masked ? (
      <input className={`${INPUT} font-mono`} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete="off" />
    ) : (
      <div className="flex min-h-[44px] items-center justify-between rounded-lg border border-stone-200 bg-stone-50 px-3">
        <span className="font-mono text-sm text-stone-700">{masked}</span>
        <button type="button" onClick={onTouch} className="min-h-[40px] rounded-md px-2 text-xs font-semibold text-ruby hover:bg-[#FFF1F3]">Replace</button>
      </div>
    )}
  </Field>
);

interface Props {
  tenantId: string;
  location: { locationId: string; name: string };
  choices: RegistrationChoice[];
  /** Open straight on a flow (skips the chooser). */
  initialFlow?: RegistrationFlowId | null;
  /** Edit this device (a row from the location list) instead of registering one. */
  edit?: any | null;
  onClose: () => void;
  /** A device was registered (the modal may stay open to pair it). */
  onRegistered?: (deviceId: string | null) => void;
  /** An edit saved. */
  onSaved?: () => void;
}

/** The location's SUNBAY merchant, entered WITH the CPad so one is never registered without the other. */
interface SbMerchant { merchantId: string; storeId: string }

/** The SUNBAY CPad Pay form — name / terminal serial / connection, and the location's merchant ID. */
const SunbayForm: React.FC<{
  s: SunbayDeviceForm; setS: (patch: Partial<SunbayDeviceForm>) => void; creating: boolean;
  merchant: SbMerchant; setMerchant: (patch: Partial<SbMerchant>) => void;
  merchantErr: { merchantId?: string; storeId?: string }; merchantSaved: boolean | null; locationName: string;
}> = ({ s, setS, creating, merchant, setMerchant, merchantErr, merchantSaved, locationName }) => (
  <>
  <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="rd-sunbay">
    <div className="space-y-3">
      <Field label="Device name" error={s.deviceName || !creating ? (s.deviceName.trim() ? null : 'Required') : null}>
        <input className={INPUT} value={s.deviceName} onChange={(e) => setS({ deviceName: e.target.value })} placeholder="Customer screen" maxLength={60} data-testid="sb-name" />
      </Field>
      <Field label="Terminal serial number" error={s.serialNumber || !creating ? (s.serialNumber.trim() ? null : 'Enter the terminal serial number from the CPad Pay') : null}>
        <input className={`${INPUT} font-mono`} value={s.serialNumber} onChange={(e) => setS({ serialNumber: e.target.value })} placeholder="From the CPad Pay" data-testid="sb-serial" />
      </Field>
    </div>
    <div className="space-y-3">
      <Field label="Connection">
        <Segmented ariaLabel="Connection" value={s.connection} options={SUNBAY_CONNECTIONS.map((c) => ({ id: c.id, label: c.label }))}
          onChange={(v) => setS({ connection: v as SunbayConnection })} />
      </Field>
    </div>
  </div>
  <div className="mt-4 rounded-xl border border-stone-200 p-3" data-testid="rd-sunbay-merchant">
    <div className="mb-2 text-xs font-semibold text-stone-700">
      SUNBAY merchant · {locationName}
      <span className="ml-1 font-normal text-stone-500">
        {merchantSaved === null ? '' : merchantSaved ? '— saved; every CPad at this location uses it' : '— required: the CPad cannot take a payment without it'}
      </span>
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field label="Merchant ID" error={merchant.merchantId ? merchantErr.merchantId || null : null}>
        <input className={`${INPUT} font-mono`} value={merchant.merchantId} onChange={(e) => setMerchant({ merchantId: e.target.value })}
          placeholder="M0123456789" autoComplete="off" disabled={merchantSaved === null} data-testid="sb-merchant-id" />
      </Field>
      <Field label="Store ID (optional)" error={merchant.storeId ? merchantErr.storeId || null : null}>
        <input className={`${INPUT} font-mono`} value={merchant.storeId} onChange={(e) => setMerchant({ storeId: e.target.value })}
          placeholder="S0123456789" autoComplete="off" disabled={merchantSaved === null} data-testid="sb-store-id" />
      </Field>
    </div>
  </div>
  </>
);

/** The MagTek DynaFlex II form — name / model / serial, and ONE UniGate status line. */
const MagtekForm: React.FC<{
  tenantId: string; locationId: string; m: MForm; setMF: (k: keyof MForm, v: string) => void;
  problems: Partial<Record<keyof MForm, string>>; creating: boolean;
}> = ({ tenantId, locationId, m, setMF, problems, creating }) => {
  const { status } = useLocationUnigate(tenantId, locationId);
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="rd-magtek">
      <div className="space-y-3">
        <Field label="Device name" error={m.deviceName || !creating ? (problems.deviceName || null) : null}>
          <input className={INPUT} value={m.deviceName} onChange={(e) => setMF('deviceName', e.target.value)} placeholder="Front counter" maxLength={60} data-testid="mt-name" />
        </Field>
        <Field label="Serial" error={m.serial || !creating ? (problems.serial || null) : null}>
          <input className={`${INPUT} font-mono`} value={m.serial} onChange={(e) => setMF('serial', e.target.value)} placeholder="Sticker serial" data-testid="mt-serial" />
        </Field>
      </div>
      <div className="space-y-3">
        <Field label="Model" tip={creating ? undefined : 'Changes only while the reader is on no till.'}>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented ariaLabel="Model" value={m.model} options={MAGTEK_MODELS.map((x) => ({ id: x.id, label: x.label }))} onChange={(v) => setMF('model', v)} />
            <span data-testid="mt-credit-only" className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-medium text-stone-600">Credit only</span>
          </div>
        </Field>
        <div className="flex min-h-[44px] items-center"><UnigateLine status={status} /></div>
      </div>
    </div>
  );
};

export const RegisterDeviceModal: React.FC<Props> = ({ tenantId, location, choices, initialFlow, edit, onClose, onRegistered, onSaved }) => {
  const editFlow: RegistrationFlowId | null = edit
    ? (String(edit.deviceClass) === 'dejavoo_rubytill_pay' ? 'tap_to_pay' : isMagtekDevice(edit) ? 'magtek' : isSunbayDevice(edit) ? 'sunbay' : 'p_series') : null;
  const [step, setStep] = useState<'choose' | RegistrationFlowId>(editFlow || initialFlow || 'choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const creating = !edit;

  // P series
  const [f, setF] = useState<PForm>(() => (edit ? formFromDevice(edit) : {
    deviceName: '', model: '', route: 'dejavoo_spin_usb', serial: '', tpn: '', registerId: '', authKey: '', iposApiKey: '', iposSecretKey: '',
  }));
  const set = (k: keyof PForm, v: string) => setF((x) => ({ ...x, [k]: v }));
  const [authKeyTouched, setAuthKeyTouched] = useState(false);
  const [iposTouched, setIposTouched] = useState(false);
  const [usePlatformKeys, setUsePlatformKeys] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [catalog, setCatalog] = useState<any[] | null>(null);
  // Tap to Pay
  const [mode, setMode] = useState<RtpayRegisterMode>('charge_only');
  const [merchantCode, setMerchantCode] = useState('');
  const [mcTouched, setMcTouched] = useState(false);
  const [rtpay, setRtpay] = useState<any | null>(null);
  // MagTek
  const [m, setM] = useState<MForm>(() => (edit && isMagtekDevice(edit) ? magtekFormFromDevice(edit) : { deviceName: '', model: '', serial: '' }));
  const setMF = (k: keyof MForm, v: string) => setM((x) => ({ ...x, [k]: v }));
  // SUNBAY
  const [sb, setSb] = useState<SunbayDeviceForm>(() => (edit && isSunbayDevice(edit) ? sunbayFormFromDevice(edit) : emptySunbayForm()));
  const setSbF = (patch: Partial<SunbayDeviceForm>) => setSb((x) => ({ ...x, ...patch }));
  const [sbMerchant, setSbMerchant] = useState<SbMerchant>({ merchantId: '', storeId: '' });
  const [sbMerchantBase, setSbMerchantBase] = useState<SbMerchant | null>(null); // null = not loaded
  const setSbM = (patch: Partial<SbMerchant>) => setSbMerchant((x) => ({ ...x, ...patch }));
  useEffect(() => {
    if (step !== 'sunbay') return;
    let live = true;
    api.posNmi.getLocationSunbay(tenantId, location.locationId)
      .then((st: any) => {
        if (!live) return;
        const b = { merchantId: st?.merchantId || '', storeId: st?.storeId || '' };
        setSbMerchantBase(b); setSbMerchant(b);
      })
      .catch(() => { if (live) { setSbMerchantBase({ merchantId: '', storeId: '' }); } });
    return () => { live = false; };
  }, [step, tenantId, location.locationId]);

  const pRoutesKey = (choices.find((c) => c.id === 'p_series')?.configIds || P_ROUTES.map((r) => r.id)).join(',');
  const pRoutes = useMemo(() => pRoutesKey.split(','), [pRoutesKey]);

  useEffect(() => {
    if (!creating || step !== 'p_series' || catalog) return;
    let live = true;
    hardwareApi.listModels({ category: 'payment_device', status: 'active' })
      .then((r) => {
        if (!live) return;
        const items = (r.items || []).filter((m: any) => configIdsOf(m).some((id) => pRoutes.includes(id)));
        setCatalog(items);
        if (items.length === 1) set('model', items[0].modelName);
      })
      .catch(() => { if (live) setCatalog([]); });
    return () => { live = false; };
  }, [creating, step, catalog, pRoutes]);

  // Edit a Tap to Pay device: its current mode and merchant code hint.
  useEffect(() => {
    if (!edit || editFlow !== 'tap_to_pay') return;
    let live = true;
    api.posNmi.getRtpay(tenantId, edit.deviceId).then((r: any) => {
      if (!live) return;
      setRtpay(r);
      if (r?.rtpay?.mode === 'customer_display' || r?.rtpay?.mode === 'charge_only') setMode(r.rtpay.mode);
    }).catch(() => {});
    return () => { live = false; };
  }, [edit, editFlow, tenantId]);

  const selected = useMemo(() => (catalog || []).find((m) => m.modelName === f.model) || null, [catalog, f.model]);
  const routes = P_ROUTES.filter((r) => pRoutes.includes(r.id) && (!selected || configIdsOf(selected).includes(r.id)));
  useEffect(() => { if (creating && routes.length && !routes.some((r) => r.id === f.route)) set('route', routes[0].id); }, [creating, routes, f.route]);
  const cloud = f.route === 'dejavoo_spin_cloud';

  const base = edit ? formFromDevice(edit) : null;
  const problems = pProblems(f, {
    creating, authKeyTouched, iposTouched, storedAuthKey: !!edit?.spinAuthKeyMasked,
    registerIdChanged: !!base && f.registerId.trim() !== base.registerId,
  });
  const show = (k: SpinKey | 'deviceName') => {
    if (k === 'spinTpn' && !f.tpn) return null;
    if ((k === 'deviceName' || k === 'serialNumber' || k === 'spinRegisterId') && creating && !(f as any)[k === 'deviceName' ? 'deviceName' : k === 'serialNumber' ? 'serial' : 'registerId']) return null;
    return problems[k] || null;
  };
  const editPatches = edit && editFlow === 'p_series' ? buildEditPatches(edit, f, { authKeyTouched, iposTouched, usePlatformKeys }) : [];

  // Tap to Pay
  const ttpTpnBad = !!f.tpn && !isValidTpn(f.tpn);
  const mcBad = !!merchantCode && !isValidMerchantCode(merchantCode);
  const ttpTpnChanged = !!edit && f.tpn.trim() !== (base?.tpn || '');
  const ttpNameChanged = !!edit && !!f.deviceName.trim() && f.deviceName.trim() !== (base?.deviceName || '');
  const modeChanged = !!edit && !!rtpay?.rtpay?.mode && mode !== rtpay.rtpay.mode;
  const mcChanged = mcTouched && !!merchantCode.trim();
  const ttpDirty = ttpTpnChanged || ttpNameChanged || modeChanged || mcChanged;

  const flow = choices.find((c) => c.id === step) || (editFlow ? choices.find((c) => c.id === editFlow) : undefined);
  const stepLabel = edit ? `${edit.deviceName || 'Device'} · ${deviceTypeLabel(edit)}` : flow?.title || null;

  const mProblems = magtekProblems(m);
  const mPatches = edit && editFlow === 'magtek' ? magtekEditPatches(edit, m) : [];

  const sbProblem = validateSunbayForm(sb);
  const sbPatches = edit && editFlow === 'sunbay' ? sunbayEditPatches(edit, sb) : [];
  const sbMerchantErr = merchantProblems(sbMerchant.merchantId, sbMerchant.storeId);
  const sbMerchantOk = !!sbMerchantBase && Object.keys(sbMerchantErr).length === 0;
  const sbMerchantDirty = !!sbMerchantBase && (sbMerchant.merchantId.trim() !== sbMerchantBase.merchantId || sbMerchant.storeId.trim() !== sbMerchantBase.storeId);
  const saveSbMerchant = async () => {
    if (!sbMerchantDirty) return;
    await api.posNmi.putLocationSunbay(tenantId, location.locationId, {
      merchantId: sbMerchant.merchantId.trim(), ...(sbMerchant.storeId.trim() ? { storeId: sbMerchant.storeId.trim() } : {}),
    });
  };

  const canSubmit = step === 'sunbay'
    ? !sbProblem && sbMerchantOk && (creating || sbPatches.length > 0 || sbMerchantDirty)
    : step === 'magtek'
    ? Object.keys(mProblems).length === 0 && (creating || mPatches.length > 0)
    : step === 'p_series'
    ? Object.keys(problems).length === 0 && (creating || editPatches.length > 0)
    : step === 'tap_to_pay'
      ? (creating ? isValidTpn(f.tpn) && isValidMerchantCode(merchantCode) : ttpDirty && !ttpTpnBad && !mcBad)
      : false;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (step === 'sunbay' && creating && isCpadRtpayConnection(sb.connection)) {
        // A cabled CPad Pay runs RubyTill Pay → register it on that lane and show its pairing code.
        await saveSbMerchant();
        const resp = await api.posNmi.registerEmvDeviceToLocation(tenantId, location.locationId, buildCpadRtpayBody(sb));
        const id = resp?.device?.deviceId || resp?.deviceId || null;
        // The new device lands in its reader slot, which issues THE pairing code (live
        // countdown, watches the pairing). Issuing one here too would void it at once.
        toastService.success('CPad registered — the RubyTill Pay pairing code is on the reader slot.');
        onRegistered?.(id);
        onClose();
      } else if (step === 'sunbay' && creating) {
        await saveSbMerchant(); // merchant first: a CPad never exists at a location without one
        const resp = await api.posNmi.registerEmvDeviceToLocation(tenantId, location.locationId, buildSunbayDeviceBody(sb) as any);
        const id = resp?.device?.deviceId || resp?.deviceId || null;
        toastService.success('Card reader registered — press Save card readers.');
        onRegistered?.(id);
        onClose();
      } else if (step === 'sunbay' && edit) {
        await saveSbMerchant();
        for (const body of sbPatches) await api.posNmi.updateEmvDevice(tenantId, location.locationId, edit.deviceId, body as any);
        toastService.success('Device updated.');
        onSaved?.();
        onClose();
      } else if (step === 'magtek' && creating) {
        const resp = await api.posNmi.registerEmvDeviceToLocation(tenantId, location.locationId, magtekRegisterBody(m) as any);
        const id = resp?.device?.deviceId || resp?.deviceId || null;
        toastService.success('Card reader registered — press Save card readers.');
        onRegistered?.(id);
        onClose();
      } else if (step === 'magtek' && edit) {
        for (const body of mPatches) await api.posNmi.updateEmvDevice(tenantId, location.locationId, edit.deviceId, body as any);
        toastService.success('Device updated.');
        onSaved?.();
        onClose();
      } else if (step === 'p_series' && creating) {
        const resp = await api.posNmi.registerEmvDeviceToLocation(tenantId, location.locationId, pSeriesRegisterBody(f) as any);
        const id = resp?.device?.deviceId || resp?.deviceId || null;
        toastService.success('Card reader registered — press Save card readers.');
        onRegistered?.(id);
        onClose();
      } else if (step === 'p_series' && edit) {
        for (const body of editPatches) await api.posNmi.updateEmvDevice(tenantId, location.locationId, edit.deviceId, body as any);
        toastService.success('Device updated.');
        onSaved?.();
        onClose();
      } else if (step === 'tap_to_pay' && creating) {
        const resp = await api.posNmi.registerEmvDeviceToLocation(tenantId, location.locationId,
          tapToPayRegisterBody({ deviceName: f.deviceName, tpn: f.tpn, merchantCode, mode }));
        const id = resp?.device?.deviceId || resp?.deviceId || null;
        // Same as the CPad: the reader slot issues the one pairing code.
        toastService.success('Tap to Pay registered — the pairing code is on the reader slot.');
        onRegistered?.(id);
        onClose();
      } else if (step === 'tap_to_pay' && edit) {
        if (ttpNameChanged) await api.posNmi.updateEmvDevice(tenantId, location.locationId, edit.deviceId, { deviceName: f.deviceName.trim() });
        if (ttpTpnChanged) await api.posNmi.updateEmvDevice(tenantId, location.locationId, edit.deviceId, { deviceConfig: { spinTpn: f.tpn.trim() } });
        if (modeChanged || mcChanged) {
          await api.posNmi.putRtpay(tenantId, edit.deviceId, { ...(modeChanged ? { mode } : {}), ...(mcChanged ? { merchantCode: merchantCode.trim() } : {}) });
        }
        toastService.success('Device updated.');
        onSaved?.();
        onClose();
      }
    } catch (e: any) {
      setError(explainPaymentError(e, edit ? 'The device was not updated.' : 'The device was not registered.'));
    } finally {
      setBusy(false);
    }
  };

  const ownIpos = !!edit?.hasOwnIposKeys;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={edit ? 'Edit payment device' : 'Register payment device'}>
      <div className="w-full max-w-[860px] overflow-hidden rounded-2xl bg-white shadow-2xl" data-testid="register-device-modal">
        <header className="flex items-center gap-3 bg-black px-5 py-3.5 text-white">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ruby text-white">{flow ? ICON[flow.id] : <CreditCard size={18} />}</span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[15px] font-semibold">{edit ? 'Edit payment device' : 'Register payment device'}</h2>
            {stepLabel && <div className="truncate text-xs text-stone-400">{stepLabel}</div>}
          </div>
          <span data-testid="rd-location" className="inline-flex items-center gap-1 rounded-full border border-stone-700 px-2.5 py-1 text-[11px] text-stone-300">
            <MapPin size={11} /> {location.name}
          </span>
          <button type="button" aria-label="Close" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-lg text-stone-400 hover:bg-white/10 hover:text-white"><X size={18} /></button>
        </header>
        <div style={RUBY_UNDERLINE} />

        <div className="p-5">
          {step === 'choose' && (
            <div className="grid gap-3 sm:grid-cols-3" data-testid="rd-choose">
              {choices.map((c) => (
                <button key={c.id} type="button" disabled={!c.enabled} data-testid={`cs-flow-${c.id}`}
                  onClick={() => { setError(null); setStep(c.id); }}
                  className="flex min-h-[64px] items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 text-left hover:border-ruby hover:bg-[#FFF1F3] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-stone-200 disabled:hover:bg-white">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#FFF1F3] text-ruby">{ICON[c.id]}</span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-semibold text-stone-900">{c.title.replace(/ \(SoftPOS\)$/, '')}</span>
                    <span className="block truncate text-[11.5px] text-stone-500">{c.enabled ? SHORT[c.id] : 'Coming next'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {step === 'p_series' && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="rd-p-series">
              <div className="space-y-3">
                <Field label="Device name" error={show('deviceName')}>
                  <input className={INPUT} value={f.deviceName} onChange={(e) => set('deviceName', e.target.value)} placeholder="Front counter" maxLength={60} />
                </Field>
                <Field label="Model">
                  {creating ? (
                    <ReaderPicker ariaLabel="Model" value={f.model || null}
                      placeholder={catalog === null ? 'Loading…' : catalog.length ? 'Pick a model' : 'No P-series model in the catalog'}
                      options={(catalog || []).map((m) => ({ id: m.modelName, label: m.modelName, sub: m.manufacturer }))}
                      onChange={(v) => set('model', v || '')} disabled={!catalog?.length} />
                  ) : (
                    <div className="flex min-h-[44px] items-center rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm text-stone-700">{f.model || '—'}</div>
                  )}
                </Field>
                <Field label="Connection" tip={creating ? undefined : 'Fixed after registration — register it again to change the route.'}>
                  {creating ? (
                    <Segmented ariaLabel="Connection" value={f.route} options={routes.length ? routes : P_ROUTES.filter((r) => pRoutes.includes(r.id))} onChange={(v) => set('route', v)} />
                  ) : (
                    <div data-testid="rd-route-readonly" className="flex min-h-[44px] items-center rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm text-stone-700">{cloud ? 'SPIn Cloud' : 'USB'}</div>
                  )}
                </Field>
                <Field label={spec('serialNumber').label} error={show('serialNumber')}>
                  <input className={`${INPUT} font-mono`} value={f.serial} onChange={(e) => set('serial', e.target.value)} placeholder="Sticker serial" />
                </Field>
              </div>
              <div className="space-y-3">
                <Field label={spec('spinTpn').label} hint="Used for refunds" error={show('spinTpn')}>
                  <input className={`${INPUT} font-mono`} inputMode="numeric" value={f.tpn} onChange={(e) => set('tpn', e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="12-digit TPN" />
                </Field>
                <Field label={spec('spinRegisterId').label} error={show('spinRegisterId')} tip="S.T.E.A.M → Edit Parameters → SPIn. Needed on USB too — the till finds the reader by it.">
                  <input className={`${INPUT} font-mono`} value={f.registerId} onChange={(e) => set('registerId', e.target.value)} placeholder="e.g. 9341901" />
                </Field>
                <Secret label={spec('spinAuthKey').label} masked={edit?.spinAuthKeyMasked || null}
                  touched={authKeyTouched || creating} onTouch={() => setAuthKeyTouched(true)}
                  value={f.authKey} onChange={(v) => { setAuthKeyTouched(true); set('authKey', v); }}
                  placeholder={cloud ? 'Short key, not the Auth Token' : 'Optional on USB'}
                  error={(authKeyTouched || (creating && f.authKey) || problems.spinAuthKey?.startsWith('Re-enter')) ? problems.spinAuthKey : null}
                  tip="Same screen as the Register Id. Not the long Auth Token." />
                <div>
                  <button type="button" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced} data-testid="rd-advanced"
                    className="inline-flex min-h-[44px] items-center gap-1 text-xs font-semibold text-stone-600 hover:text-stone-900">
                    {advanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} iPOS keys
                    <span className="ml-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10.5px] font-medium text-stone-600">
                      {usePlatformKeys ? 'platform (on save)' : ownIpos ? 'own' : 'platform'}
                    </span>
                  </button>
                  {advanced && (
                    <div className="mt-1 grid grid-cols-2 gap-3">
                      <Secret label={spec('iposApiKey').label} masked={ownIpos && !usePlatformKeys ? (edit?.iposApiKeyMasked || 'Set') : null}
                        touched={iposTouched || creating || !ownIpos} onTouch={() => { setIposTouched(true); setUsePlatformKeys(false); }}
                        value={f.iposApiKey} onChange={(v) => { setIposTouched(true); setUsePlatformKeys(false); set('iposApiKey', v); }}
                        placeholder="Blank = platform keys" error={problems.iposApiKey} />
                      <Secret label={spec('iposSecretKey').label} masked={ownIpos && !usePlatformKeys ? (edit?.iposSecretKeyMasked || 'Set') : null}
                        touched={iposTouched || creating || !ownIpos} onTouch={() => { setIposTouched(true); setUsePlatformKeys(false); }}
                        value={f.iposSecretKey} onChange={(v) => { setIposTouched(true); setUsePlatformKeys(false); set('iposSecretKey', v); }}
                        placeholder="Blank = platform keys" error={problems.iposSecretKey} />
                      {ownIpos && !usePlatformKeys && (
                        <button type="button" onClick={() => { setUsePlatformKeys(true); setIposTouched(false); set('iposApiKey', ''); set('iposSecretKey', ''); }}
                          className="col-span-2 min-h-[44px] justify-self-start rounded-lg px-2 text-xs font-semibold text-ruby hover:bg-[#FFF1F3]">
                          Use platform keys instead
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 'sunbay' && (
            <SunbayForm s={sb} setS={setSbF} creating={creating} merchant={sbMerchant} setMerchant={setSbM}
              merchantErr={sbMerchantErr} merchantSaved={sbMerchantBase ? !!sbMerchantBase.merchantId : null} locationName={location.name} />
          )}

          {step === 'magtek' && (
            <MagtekForm tenantId={tenantId} locationId={location.locationId} m={m} setMF={setMF} problems={mProblems} creating={creating} />
          )}

          {step === 'tap_to_pay' && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="rd-tap-to-pay">
              <div className="space-y-3">
                <Field label="Device name"><input className={INPUT} value={f.deviceName} onChange={(e) => set('deviceName', e.target.value)} placeholder={ANDROID_TAP_TO_PAY_MODEL} maxLength={60} data-testid="ttp-name" /></Field>
                <Field label="Mode">
                  <Segmented ariaLabel="Mode" value={mode}
                    options={[{ id: 'charge_only', label: 'Charge only' }, { id: 'customer_display', label: 'Customer screen' }]}
                    onChange={(v) => setMode(v as RtpayRegisterMode)} />
                </Field>
              </div>
              <div className="space-y-3">
                <Field label="TPN" error={ttpTpnBad ? '12 digits' : null}>
                  <input className={`${INPUT} font-mono`} inputMode="numeric" value={f.tpn} onChange={(e) => set('tpn', e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="12-digit TPN" data-testid="ttp-tpn" />
                </Field>
                {creating ? (
                  <Field label="Merchant code" error={mcBad ? '6–20 digits' : null}>
                    <input className={`${INPUT} font-mono`} inputMode="numeric" autoComplete="off" value={merchantCode} onChange={(e) => setMerchantCode(e.target.value.replace(/\D/g, '').slice(0, 20))} placeholder="Merchant code" data-testid="ttp-merchant-code" />
                  </Field>
                ) : (
                  <Secret label="Merchant code" masked={rtpay?.rtpay?.merchantCodeLast4 ? `••••${rtpay.rtpay.merchantCodeLast4}` : 'Set'}
                    touched={mcTouched} onTouch={() => setMcTouched(true)} value={merchantCode}
                    onChange={(v) => setMerchantCode(v.replace(/\D/g, '').slice(0, 20))} placeholder="Merchant code" error={mcBad ? '6–20 digits' : null} />
                )}
              </div>
            </div>
          )}

          {error && <p role="alert" className="mt-3 text-[12px] text-ruby-deep">{error}</p>}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3">
          {step !== 'choose' && creating && !initialFlow && (
            <button type="button" onClick={() => { setError(null); setStep('choose'); }} className="mr-auto min-h-[44px] rounded-lg px-3 text-sm font-medium text-stone-600 hover:bg-stone-100">Back</button>
          )}
          <button type="button" onClick={onClose} className="min-h-[44px] rounded-lg px-4 text-sm font-medium text-stone-600 hover:bg-stone-100">Cancel</button>
          {step !== 'choose' && (
            <button type="button" data-testid="rd-submit" disabled={!canSubmit || busy} onClick={submit}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-5 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
              {busy && <RubyGemMark size={16} drawing color="currentColor" />} {edit ? 'Save' : 'Register'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default RegisterDeviceModal;
